import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api, ApiError, LABEL_PALETTE, type TicketStatusLabel } from '../lib/api'
import { Banner } from '../ui'
import { ReadOnlyNotice } from './ReadOnlyNotice'

/**
 * The four-stage ticket lifecycle (open → pending → resolved → closed) itself is fixed — SLA
 * escalation, resolution analytics, and automation-rule conditions all key off exactly these
 * four stages, and none of that needs to change for the product to feel industry-specific. What
 * genuinely differs is the *word* for each stage: a courier's "pending" ticket reads better as
 * "Awaiting parts," a clinic's as "Awaiting lab results." This screen is where that gets renamed,
 * with no code change and no risk to anything that depends on the underlying stage.
 */
export function TicketStatusSettings() {
  const queryClient = useQueryClient()
  const [editingKind, setEditingKind] = useState<string | null>(null)
  const [editLabel, setEditLabel] = useState('')
  const [editColor, setEditColor] = useState('')

  const query = useQuery({ queryKey: ['ticket-statuses'], queryFn: api.ticketStatuses, retry: false })
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ticket-statuses'] })

  const save = useMutation({
    mutationFn: (statuses: TicketStatusLabel[]) => api.saveTicketStatuses(statuses),
    onSuccess: async () => {
      setEditingKind(null)
      await refresh()
    },
  })

  if (query.isPending) return <div className="cad-card" style={{ height: '200px' }} />
  if (query.isError) {
    const message = query.error instanceof ApiError ? query.error.message : 'Ticket statuses unavailable.'
    return (
      <div className="cad-card">
        <p className="cad-muted">{message}</p>
      </div>
    )
  }

  const { statuses, canManage } = query.data!
  const saveError = save.error instanceof ApiError ? save.error.message : undefined

  const startEdit = (status: TicketStatusLabel) => {
    setEditingKind(status.kind)
    setEditLabel(status.label)
    setEditColor(status.color)
  }

  const saveEdit = () => {
    if (!editLabel.trim() || !editingKind) return
    const next = statuses.map((s) => (s.kind === editingKind ? { ...s, label: editLabel.trim(), color: editColor } : s))
    save.mutate(next)
  }

  return (
    <>
      {!canManage && <ReadOnlyNotice what="ticket status labels" />}
      {/* The active tab above already says "Ticket statuses". */}
      <p className="cad-muted" style={{ maxWidth: '62ch' }}>
        A ticket always moves through the same four stages — nothing about SLA tracking or
        reporting changes here. Only the word and color shown for each stage is yours to set,
        so the language matches how your team actually talks about work.
      </p>

      {saveError && <Banner tone="error">{saveError}</Banner>}

      <section className="cad-card">
        <div className="cad-grid-frame">
          <table className="cad-table">
            <thead>
              <tr>
                <th>Stage</th>
                <th>Shown as</th>
                <th>Color</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {statuses.map((status) => (
                <tr key={status.kind}>
                  <td className="cad-muted" style={{ textTransform: 'capitalize' }}>{status.kind}</td>
                  <td>
                    {editingKind === status.kind ? (
                      <input
                        className="cad-input"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        style={{ maxWidth: '220px' }}
                      />
                    ) : (
                      <span
                        className="cad-pill"
                        style={{
                          // The seeded defaults are `var(--cad-bad)` etc, not hex — `${color}22`
                          // (the hex-alpha trick LabelSettings.tsx uses, safe there because label
                          // colors are always hex from LABEL_PALETTE) produces `var(--cad-bad)22`,
                          // which is invalid CSS and silently dropped, leaving every unrenamed row
                          // background-less. color-mix() works for both a var() reference and a
                          // literal hex value.
                          background: `color-mix(in srgb, ${status.color} 13%, transparent)`,
                          color: status.color,
                        }}
                      >
                        {status.label}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingKind === status.kind ? (
                      <div className="row gap-1 wrap">
                        {LABEL_PALETTE.map((swatch) => (
                          <button
                            key={swatch}
                            type="button"
                            onClick={() => setEditColor(swatch)}
                            aria-label={`Choose ${swatch}`}
                            aria-pressed={editColor === swatch}
                            className="cad-swatch"
                            data-selected={editColor === swatch}
                            style={{ background: swatch }}
                          />
                        ))}
                      </div>
                    ) : (
                      <span className="cad-swatch" style={{ background: status.color }} aria-hidden="true" />
                    )}
                  </td>
                  {canManage && (
                    <td>
                      {editingKind === status.kind ? (
                        <div className="row gap-2">
                          <button
                            type="button"
                            className="cad-btn"
                            data-variant="text"
                            disabled={!editLabel.trim() || save.isPending}
                            onClick={saveEdit}
                          >
                            {save.isPending ? 'Saving…' : 'Save'}
                          </button>
                          <button type="button" className="cad-btn" data-variant="text" onClick={() => setEditingKind(null)}>
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button type="button" className="cad-btn" data-variant="text" onClick={() => startEdit(status)}>
                          Rename
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </>
  )
}
